import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';

const Layout = ({ children, userProfile }) => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [openSubmenus, setOpenSubmenus] = useState({});
    const [userRole, setUserRole] = useState(null);
    const [userName, setUserName] = useState('');
    const [displayName, setDisplayName] = useState(''); // First part before hyphen
    const [roleSuffix, setRoleSuffix] = useState(''); // Second part after hyphen
    const location = useLocation();
    const navigate = useNavigate();

    // Refs
    const userMenuRef = useRef(null);
    const sidebarRef = useRef(null);

    // Function to decrypt cookies
    const decryptValue = (encryptedValue) => {
        if (!encryptedValue) return null;
        try {
            const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY || 'your-secret-key';
            const bytes = CryptoJS.AES.decrypt(encryptedValue, encryptionKey);
            return bytes.toString(CryptoJS.enc.Utf8);
        } catch (error) {
            console.error("Decryption error:", error);
            return null;
        }
    };

    // Function to split username at hyphen
    const splitUserName = (fullName) => {
        if (!fullName) return { display: 'User', suffix: '' };
        
        const parts = fullName.split('-');
        if (parts.length > 1) {
            return {
                display: parts[0].trim(),
                suffix: parts.slice(1).join('-').trim() // Join remaining parts in case of multiple hyphens
            };
        }
        return {
            display: fullName,
            suffix: ''
        };
    };

    // Load user data from cookies on component mount
    useEffect(() => {
        const loadUserData = () => {
            const role = decryptValue(Cookies.get('from'));
            const name = decryptValue(Cookies.get('u_nm')) || 'User';

            setUserRole(role);
            setUserName(name);
            
            // Split the username
            const { display, suffix } = splitUserName(name);
            setDisplayName(display);
            setRoleSuffix(suffix);

            // Also check if userProfile prop is provided (from App.js)
            if (userProfile && userProfile.role_name) {
                setUserRole(userProfile.role_name);
            }
            if (userProfile && userProfile.user_name) {
                setUserName(userProfile.user_name);
                // Split the username from profile
                const { display, suffix } = splitUserName(userProfile.user_name);
                setDisplayName(display);
                setRoleSuffix(suffix);
            }
        };

        loadUserData();
    }, [userProfile]);

    // Function to remove ALL cookies
    const removeAllCookies = () => {
        try {
            const cookies = document.cookie.split(";");
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i];
                const eqPos = cookie.indexOf("=");
                const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
            }
            localStorage.clear();
            sessionStorage.clear();
        } catch (error) {
            console.error("Error clearing cookies:", error);
        }
    };

    // Enhanced logout function
    const handleLogout = () => {
        removeAllCookies();
        setShowUserMenu(false);
        window.location.href = '/EMS';
    };

    // Check if device is mobile
    useEffect(() => {
        const checkDevice = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile) {
                setSidebarOpen(false);
            }
        };

        checkDevice();
        window.addEventListener('resize', checkDevice);
        return () => window.removeEventListener('resize', checkDevice);
    }, []);

    // Close sidebar when route changes on mobile
    useEffect(() => {
        if (isMobile) {
            setSidebarOpen(false);
        }
    }, [location.pathname, isMobile]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
            if (isMobile && sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                setSidebarOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isMobile, sidebarOpen]);

    // Define all possible navigation items
    const allNavigation = [
        {
            id: "dashboard",
            name: "Dashboard",
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4" />
                </svg>
            ),
            path: "/dashboard",
            roles: ["ADMINISTRATOR", "ACCOUNTANT", "TRUSTEE"] // Add all roles that should see this
        },
        {
            id: "master",
            name: "Master",
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
            ),
            roles: ["ADMINISTRATOR"], // Only ADMINISTRATOR can see Master
            children: [
                {
                    id: "attribute-master",
                    name: "Attribute Master",
                    path: "/attributeMaster",
                    roles: ["ADMINISTRATOR"],
                    icon: (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    )
                },
                {
                    id: "coordinator-details",
                    name: "Coordinator Details",
                    path: "/coordinatorDetails",
                    roles: ["ADMINISTRATOR"],
                    icon: (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0c-.66 0-1.293.092-1.896.263M12 4.354A4 4 0 0012 4m0 0a4 4 0 00-4 4m8 0a4 4 0 00-4-4m0 0a4 4 0 00-4 4" />
                        </svg>
                    )
                }
            ]
        },
        {
            id: "events",
            name: "Events",
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            ),
            path: "/events",
            roles: ["ADMINISTRATOR", "ACCOUNTANT", "TRUSTEE"] // Both ADMINISTRATOR and COORDINATOR can see Events
        },
        {
            id: "payment",
            name: "Payment Overview",
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-5 h-5"
                >
                    <rect x="2.5" y="5" width="19" height="14" rx="3" />
                    <path d="M2.5 9h19" />
                    <circle cx="9" cy="15" r="2" />
                    <circle cx="14" cy="15" r="2" />
                </svg>
            ),
            path: "/payment",
            roles: ["ADMINISTRATOR", "ACCOUNTANT", "TRUSTEE"] // ADMINISTRATOR and ACCOUNTANT can see Payment
        },
        {
            id: "reports",
            name: "Reports",
            icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            ),
            path: "/Reports",
            roles: ["ADMINISTRATOR", "ACCOUNTANT", "TRUSTEE"] // ADMINISTRATOR and ACCOUNTANT can see Reports
        },
    ];

    const getFilteredNavigation = () => {
        if (!userRole) return [];

        // Normalize role to uppercase for comparison
        const normalizedRole = userRole ? userRole.toUpperCase() : '';

        return allNavigation.filter(item => {
            // Check if user's role is in the item's allowed roles array
            return item.roles && item.roles.includes(normalizedRole);
        }).map(item => {
            // Filter children as well if they exist
            if (item.children) {
                return {
                    ...item,
                    children: item.children.filter(child =>
                        child.roles && child.roles.includes(normalizedRole)
                    )
                };
            }
            return item;
        }).filter(item => {
            // Remove parent menu if it has no visible children
            if (item.children && item.children.length === 0) {
                return false;
            }
            return true;
        });
    };

    const navigation = getFilteredNavigation();

    const getActiveView = () => {
        const currentPath = location.pathname;
        const navItem = navigation.find((item) => currentPath === item.path);
        if (navItem) return navItem.id;

        for (const item of navigation) {
            if (item.children) {
                const childItem = item.children.find(child => currentPath === child.path);
                if (childItem) return childItem.id;
            }
        }

        return currentPath === "/EMS" ? "dashboard" : "404";
    };

    const activeView = getActiveView();

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
    };

    const closeAllSubmenus = () => {
        setOpenSubmenus({});
    };

    const isMenuActive = (menuId) => {
        const menu = navigation.find(item => item.id === menuId);
        if (menu && menu.children) {
            return menu.children.some(child => child.id === activeView);
        }
        return false;
    };

    const toggleSubmenu = (menuId) => {
        setOpenSubmenus(prev => {
            const isCurrentlyOpen = prev[menuId];
            const newState = { ...prev };
            if (isCurrentlyOpen) {
                delete newState[menuId];
            } else {
                newState[menuId] = true;
            }
            return newState;
        });
    };

    const handleMenuItemClick = () => {
        closeAllSubmenus();
        if (isMobile) {
            setSidebarOpen(false);
        }
    };

    // Auto-open submenu when child is active
    useEffect(() => {
        const newOpenSubmenus = { ...openSubmenus };
        let hasChanged = false;

        navigation.forEach(item => {
            if (item.children && isMenuActive(item.id)) {
                if (!newOpenSubmenus[item.id]) {
                    newOpenSubmenus[item.id] = true;
                    hasChanged = true;
                }
            }
        });

        if (hasChanged) {
            setOpenSubmenus(newOpenSubmenus);
        }
    }, [activeView, location.pathname, navigation]);

    // Get user initials for avatar (using display name)
    const getUserInitials = () => {
        if (!displayName) return 'US';
        return displayName
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    // Get role display name (combining userRole and roleSuffix if available)
    const getRoleDisplayName = () => {
        if (roleSuffix) {
            // If we have a suffix from the split, use it with proper formatting
            return roleSuffix.charAt(0).toUpperCase() + roleSuffix.slice(1).toLowerCase();
        }
        // Fallback to userRole if no suffix
        if (!userRole) return 'User';
        return userRole.charAt(0).toUpperCase() + userRole.slice(1).toLowerCase();
    };

    return (
        <div className="flex h-screen bg-gray-50">
            {/* Sidebar */}
            <div
                ref={sidebarRef}
                className={`${sidebarOpen ? "w-64" : "w-0 md:w-20"
                    } transition-all duration-300 bg-white border-r border-gray-200 shadow-sm flex flex-col fixed md:relative z-30 h-full`}
            >
                <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center">
                        {sidebarOpen && (
                            <div className="w-48 h-10 rounded-2xl flex items-center justify-center transition-all duration-300">
                                <img
                                    src="/EMS/Images/Logo.png"
                                    alt="DeskApp Logo"
                                    className="w-full h-full object-contain drop-shadow-md"
                                />
                            </div>
                        )}

                        <div className="ml-auto flex items-center">
                            {isMobile && sidebarOpen && (
                                <button
                                    onClick={closeSidebar}
                                    className="p-2 rounded-lg hover:bg-gray-100 transition mr-2"
                                >
                                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}

                            {!isMobile && (
                                <button
                                    onClick={toggleSidebar}
                                    className="p-2 rounded-lg hover:bg-gray-100 transition"
                                >
                                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <nav className="mt-4 flex-1 overflow-y-auto md:px-4">
                    {navigation.map((item) => {
                        const isActive = activeView === item.id || isMenuActive(item.id);
                        const isSubmenuOpen = openSubmenus[item.id];

                        if (item.children) {
                            return (
                                <div key={item.id} className="mb-1">
                                    <button
                                        onClick={() => toggleSubmenu(item.id)}
                                        className={`group flex items-center justify-between w-full px-3 py-3 my-1 rounded-lg transition-all duration-200 ${isActive
                                            ? 'bg-blue-600 text-white shadow-sm'
                                            : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                                            }`}
                                    >
                                        <div className="flex items-center">
                                            <span className={`${isActive ? "text-white" : "text-gray-400 group-hover:text-blue-600"}`}>
                                                {item.icon}
                                            </span>
                                            {sidebarOpen && <span className="ml-3 font-medium">{item.name}</span>}
                                        </div>
                                        {sidebarOpen && (
                                            <svg
                                                className={`w-4 h-4 transition-transform duration-200 ${isSubmenuOpen ? 'rotate-180' : ''}`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        )}
                                    </button>

                                    {/* Animated Submenu */}
                                    {sidebarOpen && (
                                        <div
                                            className={`overflow-hidden transition-all duration-300 ease-in-out ${isSubmenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                                                }`}
                                        >
                                            <div className="ml-4 mt-1 space-y-1 pl-3 py-1">
                                                {item.children.map((child) => {
                                                    const isChildActive = activeView === child.id;
                                                    return (
                                                        <Link
                                                            key={child.id}
                                                            to={child.path}
                                                            className={`flex items-center px-3 py-2 rounded-lg transition-all duration-200 group ${isChildActive
                                                                ? 'bg-blue-50 text-blue-600 font-medium border-l-2 border-blue-500 -ml-0.5'
                                                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                                                                }`}
                                                            onClick={() => isMobile && setSidebarOpen(false)}
                                                        >
                                                            <span className={`${isChildActive ? "text-blue-500" : "text-gray-400 group-hover:text-gray-600"} mr-2`}>
                                                                {child.icon}
                                                            </span>
                                                            <span className="text-sm">{child.name}</span>
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        } else {
                            return (
                                <Link
                                    key={item.id}
                                    to={item.path}
                                    className={`group flex items-center px-3 py-3 my-1 rounded-lg transition-all duration-200 ${isActive
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                                        }`}
                                    onClick={handleMenuItemClick}
                                >
                                    <span className={`${isActive ? "text-white" : "text-gray-400 group-hover:text-blue-600"}`}>
                                        {item.icon}
                                    </span>
                                    {sidebarOpen && <span className="ml-3 font-medium">{item.name}</span>}
                                </Link>
                            );
                        }
                    })}
                </nav>

                {/* Sidebar Footer */}
                {sidebarOpen && (
                    <footer className="border-t bg-blue-600 border-blue-700 py-4 px-2">
                        <div className="flex flex-col md:flex-row justify-center items-center gap-2">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="white"
                                className="w-5 h-5"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M6.75 3v2.25M17.25 3v2.25M3.75 8.25h16.5M5.25 6.75h13.5a1.5 1.5 0 011.5 1.5v10.5a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5V8.25a1.5 1.5 0 011.5-1.5z"
                                />
                            </svg>
                            <p className="text-white text-sm tracking-wide">
                                Event Management System
                            </p>
                        </div>
                    </footer>
                )}
            </div>

            {/* Main Section */}
            <div className="flex-1 flex flex-col overflow-hidden md:ml-0 ml-0">
                {/* Header */}
                <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-2 flex items-center justify-between">
                    <div className="flex items-center">
                        {isMobile && (
                            <button
                                onClick={toggleSidebar}
                                className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition"
                            >
                                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                        )}
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">
                                {navigation.find(x => x.id === activeView)?.name ||
                                    navigation.find(item => item.children?.some(child => child.id === activeView))?.children?.find(child => child.id === activeView)?.name ||
                                    "Dashboard"}
                            </h2>
                            <p className="text-gray-500 text-sm">Manage your events efficiently</p>
                        </div>
                    </div>

                    {/* User Menu */}
                    <div className="relative" ref={userMenuRef}>
                        <button
                            onClick={() => setShowUserMenu(!showUserMenu)}
                            className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition"
                        >
                            <div className="text-right hidden sm:block">
                                <div className="text-sm font-medium text-gray-900">{displayName || 'User'}</div>
                                <div className="text-xs text-gray-500">{getRoleDisplayName()}</div>
                            </div>
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shadow-sm">
                                <span className="text-white font-semibold">{getUserInitials()}</span>
                            </div>
                        </button>

                        {showUserMenu && (
                            <div className="absolute right-0 mt-2 w-56 bg-white shadow-lg rounded-lg border border-gray-200 py-2 z-[99999999]">
                                <div className="px-4 py-2 border-b border-gray-100">
                                    <div className="text-sm font-medium text-gray-900">{displayName || 'User'}</div>
                                    <div className="text-xs text-gray-500">{getRoleDisplayName()}</div>
                                </div>
                                <button
                                    onClick={handleLogout}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 transition-colors flex items-center"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                    </svg>
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                {/* Content Area */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Main Content */}
                    <main className="flex-1 overflow-auto p-6 bg-white m-4 rounded-lg border border-gray-200 shadow-sm">
                        {children}
                    </main>

                    {/* Main Footer */}
                    <footer className=" border-t bg-blue-600  border-gray-200 py-3.5 px-6">
                        <div className="flex flex-col md:flex-row justify-center items-center">
                            <div className="text-md text-white flex items-center">
                                © {new Date().getFullYear()} All rights reserved PSG Software Technologies
                            </div>
                        </div>
                    </footer>
                </div>
            </div>

            {/* Overlay for mobile sidebar */}
            {sidebarOpen && isMobile && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-20"
                    onClick={closeSidebar}
                />
            )}
        </div>
    );
};

export default Layout;